import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABEL = { singles: "Singles", doubles: "Doubles", men: "Men", women: "Women", mixed: "Mixed" };
const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export const LiveRecap = () => {
  const [sims, setSims] = useState(null);

  useEffect(() => {
    axios.get(`${API}/results/recap`).then((r) => setSims(r.data)).catch(() => setSims([]));
  }, []);

  if (!sims) {
    return (
      <p className="font-mono2 text-xs uppercase tracking-[0.25em] text-zinc-500 animate-pulse my-8" data-testid="live-recap-loading">
        Loading live results…
      </p>
    );
  }

  return (
    <div className="my-8 space-y-6" data-testid="live-recap">
      {sims.map((s) =>
        s.total_finishers === 0 ? (
          <aside key={s.sim_date} className="border border-white/10 bg-[#0c0c0c] p-6" data-testid={`live-recap-empty-${s.sim_date}`}>
            <p className="eyebrow mb-2">{fmtDate(s.sim_date)} Simulation</p>
            <p className="text-zinc-400 text-sm leading-relaxed">
              No times on the board yet. Raced this sim?{" "}
              <Link to="/results" className="text-volt underline underline-offset-2 hover:text-white transition-colors">Post your time</Link>{" "}
              and this recap fills in automatically.
            </p>
          </aside>
        ) : (
          <div key={s.sim_date} className="border border-volt/40 bg-[#0c0c0c]" data-testid={`live-recap-${s.sim_date}`}>
            <div className="p-6 border-b border-white/10 flex flex-wrap gap-x-10 gap-y-4 items-end justify-between">
              <div>
                <p className="eyebrow mb-1">{fmtDate(s.sim_date)} Simulation</p>
                <p className="font-head font-extrabold uppercase tracking-tight text-2xl">Live Leaderboard Recap</p>
              </div>
              <div className="flex gap-7 font-mono2 text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                <span><span className="block text-volt text-2xl font-head font-black">{s.total_finishers}</span>Finishers</span>
                <span><span className="block text-volt text-2xl font-head font-black">{s.median_time}</span>Median</span>
                <span><span className="block text-volt text-2xl font-head font-black">{s.fastest.time}</span>Fastest</span>
              </div>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-6">
              {s.boards.map((b) => (
                <div key={`${b.division}-${b.category}`} data-testid={`recap-board-${s.sim_date}-${b.division}-${b.category}`}>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2.5 flex items-center gap-1.5">
                    <Trophy size={11} className="text-volt" /> {LABEL[b.division]} · {LABEL[b.category]} · {b.finishers} finished
                  </p>
                  <ol className="space-y-1.5">
                    {b.podium.map((p, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <span className="font-head font-black text-volt w-4 shrink-0">{i + 1}</span>
                        <span className="text-white truncate">{p.name}</span>
                        <span className="ml-auto font-mono2 text-zinc-300 shrink-0">{p.time}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
            <p className="px-6 pb-5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-600">
              Full board on the <Link to="/results" className="text-volt hover:text-white transition-colors">results page</Link>
            </p>
          </div>
        )
      )}
    </div>
  );
};
